import { useState, type JSX } from 'react'
import { Voicemail as VoicemailIcon, Mail, MailOpen, Clock3, Trash2, AlertTriangle, X, RefreshCw } from 'lucide-react'

import RequirePermission from '../../components/auth/RequirePermission.js'
import VoicemailRow from '../../components/voicemail/VoicemailRow.js'
import { useMyVoicemails } from '../../hooks/useMyVoicemails.js'
import { useVoicemailsStore } from '../../store/voicemailsStore.js'
import { api } from '../../api/client.js'
import { useToast } from '../../components/system/ToastProvider.js'

type StatusFilter = 'all' | 'NEW' | 'HEARD'

const R = 'rounded-[3px]'

function formatDuration(totalSec: number): string {
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export default function VoicemailInbox(): JSX.Element {
  const toast = useToast()
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('NEW')
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const { voicemails: serverVoicemails, loading, refetch } = useMyVoicemails({
    status: statusFilter === 'all' ? null : statusFilter,
  })

  const allVoicemails = serverVoicemails
  const filteredVoicemails = serverVoicemails

  const newCount = serverVoicemails.filter((v) => v.status === 'NEW').length
  const heardCount = serverVoicemails.filter((v) => v.status === 'HEARD').length
  const totalDurationSec = serverVoicemails.reduce((sum, v) => sum + Math.round(v.durationMs / 1000), 0)

  async function handleRefresh(): Promise<void> {
    setRefreshing(true)
    try {
      await refetch()
    } finally {
      setRefreshing(false)
    }
  }

  async function markHeard(id: string): Promise<void> {
    try {
      await api.patch(`/voicemails/${id}`, { status: 'HEARD' })
      useVoicemailsStore.getState().patchVoicemail(id, { status: 'HEARD' })
      void refetch()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to mark voicemail heard')
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!confirmDeleteId) return
    const id = confirmDeleteId
    setConfirmDeleteId(null)
    try {
      await api.delete(`/voicemails/${id}`)
      useVoicemailsStore.getState().removeVoicemail(id)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete voicemail')
    }
  }

  const FILTER_TABS: { key: StatusFilter; label: string; icon: JSX.Element; count: number }[] = [
    { key: 'NEW', label: 'New', icon: <Mail className="h-3.5 w-3.5" />, count: newCount },
    { key: 'HEARD', label: 'Heard', icon: <MailOpen className="h-3.5 w-3.5" />, count: heardCount },
    { key: 'all', label: 'All', icon: <VoicemailIcon className="h-3.5 w-3.5" />, count: allVoicemails.length },
  ]

  const TILES: { label: string; value: string; dot: string; Icon: typeof Mail; hint: string }[] = [
    { label: 'Total voicemails', value: String(allVoicemails.length), dot: 'bg-[#3366FF]', Icon: VoicemailIcon, hint: `${heardCount} already heard` },
    { label: 'New', value: String(newCount), dot: 'bg-blue-500', Icon: Mail, hint: 'waiting to be played' },
    { label: 'Heard', value: String(heardCount), dot: 'bg-slate-400', Icon: MailOpen, hint: 'marked as listened' },
    { label: 'Total duration', value: formatDuration(totalDurationSec), dot: 'bg-emerald-500', Icon: Clock3, hint: 'across all messages' },
  ]

  return (
    <RequirePermission permission="voicemail:view_own">
      <div className="flex h-full flex-col bg-[#F4F6F8]">
        {/* ── Header ── */}
        <header className="flex flex-wrap items-center gap-x-5 gap-y-3 border-b border-slate-200 bg-white px-6 py-3.5">
          <div className="flex shrink-0 items-center gap-3">
            <span className={`flex h-10 w-10 items-center justify-center border border-blue-200 ${R} bg-blue-50 text-[#3366FF]`}>
              <VoicemailIcon className="h-5 w-5" />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-bold leading-tight tracking-tight text-slate-900">Voicemail Inbox</h1>
                <span className={`${R} border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600`}>New: <span className="font-mono">{newCount}</span></span>
              </div>
              <p className="mt-0.5 text-xs text-slate-500">Voicemails left for you — play, mark heard or delete.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void handleRefresh()}
            disabled={refreshing}
            className={`ml-auto flex h-9 w-9 items-center justify-center ${R} border border-slate-300 bg-white text-slate-500 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-800 disabled:opacity-40`}
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </header>

        <div className="scrollbar-thin flex-1 space-y-4 overflow-y-auto p-5">
          {/* ── Stat tiles ── */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {TILES.map((t) => (
              <div key={t.label} className={`border border-slate-200 bg-white px-4 py-3.5 ${R} shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-shadow hover:shadow-md`}>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                    <span className={`h-1.5 w-1.5 rounded-full ${t.dot}`} />
                    {t.label}
                  </span>
                  <t.Icon className="h-4 w-4 text-slate-300" />
                </div>
                <p className="mt-2 font-mono text-xl font-extrabold tabular-nums text-slate-900">{t.value}</p>
                <p className="mt-0.5 text-[11.5px] text-slate-400">{t.hint}</p>
              </div>
            ))}
          </div>

          {/* ── Filter tabs ── */}
          <div className="flex items-center gap-2" data-testid="status-filter" data-value={statusFilter}>
            {FILTER_TABS.map((tab) => {
              const active = statusFilter === tab.key
              return (
                <button
                  key={tab.key}
                  type="button"
                  aria-label={`filter-${tab.key}`}
                  data-active={active ? 'yes' : 'no'}
                  onClick={() => setStatusFilter(tab.key)}
                  className={`flex h-8 items-center gap-2 ${R} border px-3 text-[12.5px] font-semibold transition-colors ${
                    active
                      ? 'border-blue-300 bg-blue-50 text-blue-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                  <span className={`ml-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full px-1 font-mono text-[10px] font-bold ${
                    active ? 'bg-[#3366FF] text-white' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              )
            })}
          </div>

          {loading ? (
            <div data-testid="voicemail-loading" className={`${R} border border-slate-200 bg-white py-12 text-center`}>
              <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-[#3366FF]" />
              <p className="text-sm text-slate-500">Loading voicemails…</p>
            </div>
          ) : filteredVoicemails.length === 0 ? (
            <div className={`${R} border border-slate-200 bg-white py-16 text-center`}>
              <VoicemailIcon className="mx-auto mb-3 h-10 w-10 text-slate-300" />
              <p className="text-sm font-medium text-slate-500">No voicemails</p>
              <p className="mt-1 text-xs text-slate-400">New voicemails will appear here when someone leaves a message.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredVoicemails.map((vm) => (
                <VoicemailRow
                  key={vm.id}
                  voicemail={vm}
                  {...(vm.status === 'NEW' ? { onMarkHeard: markHeard } : {})}
                  onDelete={(id) => setConfirmDeleteId(id)}
                />
              ))}
            </div>
          )}

          {filteredVoicemails.length > 0 && (
            <div className={`flex items-center justify-between ${R} border border-slate-200 bg-white px-4 py-2.5 text-xs text-slate-500`}>
              <span>Showing <span className="font-mono font-semibold text-slate-700">{filteredVoicemails.length}</span> voicemail{filteredVoicemails.length !== 1 ? 's' : ''}</span>
              <span><span className="font-mono">{newCount}</span> new · <span className="font-mono">{heardCount}</span> heard</span>
            </div>
          )}
        </div>

        {confirmDeleteId && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            role="dialog"
            aria-modal="true"
            aria-label="Delete voicemail"
            data-testid="confirm-dialog"
          >
            <div className={`w-full max-w-md ${R} border border-slate-200 bg-white p-6 shadow-xl`}>
              <div className="mb-4 flex items-start gap-3">
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center ${R} bg-rose-50`}>
                  <AlertTriangle className="h-5 w-5 text-rose-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-[17px] font-bold leading-tight tracking-tight text-slate-900">
                    Delete Voicemail
                  </h3>
                  <p className="mt-1 text-[13px] text-slate-500">
                    Delete this voicemail? This cannot be undone.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setConfirmDeleteId(null)}
                  className={`${R} p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700`}
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmDeleteId(null)}
                  className={`inline-flex h-9 items-center gap-1.5 ${R} border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void confirmDelete()}
                  className={`inline-flex h-9 items-center gap-1.5 ${R} bg-red-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-red-700`}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </RequirePermission>
  )
}
